import { useState, useEffect } from "react";
import { Sparkles, Loader2, Check, Circle } from "lucide-react";

type StageLogItem = {
  stage_name: string;
  status: string;
  message: string;
  created_at?: string;
};

interface GenerationProgressProps {
  productTitle?: string;
  startedAt?: string | Date | null;
  progressMessage?: string | null;
  currentStage?: string | null;
  stageLogs?: StageLogItem[];
  onCancel?: () => void;
  showCancel?: boolean;
  embedded?: boolean;
  cancelButtonText?: string;
  mode?: string | null;
}

const LEGACY_PROGRESS_STEPS: { msg: string; label: string }[] = [
  { msg: "Preparing your photos for 3D conversion", label: "Removing background & preparing photos" },
  { msg: "Building 3D shape from your photos", label: "Generating 3D geometry" },
  { msg: "Adding colors and textures to your model", label: "Projecting high-res texture (4K PBR)" },
  { msg: "Optimizing for AR and preparing upload", label: "Refining materials & quality check" },
  { msg: "Uploading to your gallery", label: "Optimising & uploading AR model" },
];

const V2_PROGRESS_STEPS: { id: string; label: string; stages: string[] }[] = [
  {
    id: "preprocess",
    label: "Removing background & preparing photos",
    stages: ["queued", "contract_validated", "submit", "preprocess"],
  },
  {
    id: "shape",
    label: "Generating 3D geometry from your photos",
    stages: ["single-shape", "multi-shape"],
  },
  {
    id: "texture",
    label: "Projecting high-res texture (4K PBR)",
    stages: ["texture-project"],
  },
  {
    id: "paint",
    label: "Refining materials & inpainting gaps",
    stages: ["paint"],
  },
  {
    id: "qa",
    label: "Running quality validation",
    stages: ["qa-render"],
  },
  {
    id: "export",
    label: "Optimising & uploading AR model",
    stages: ["export", "promoted_master", "completed"],
  },
];

function stageToLegacyMessage(stage?: string | null): string | null {
  if (!stage) return null;
  const normalized = stage.toLowerCase();
  if (["queued", "contract_validated", "submit", "preprocess"].includes(normalized)) {
    return "Preparing your photos for 3D conversion";
  }
  if (["single-shape", "multi-shape"].includes(normalized)) {
    return "Building 3D shape from your photos";
  }
  if (normalized === "texture-project") {
    return "Adding colors and textures to your model";
  }
  if (["paint", "qa-render"].includes(normalized)) {
    return "Optimizing for AR and preparing upload";
  }
  if (["export", "promoted_master", "completed"].includes(normalized)) {
    return "Uploading to your gallery";
  }
  return null;
}

function resolveV2StepIndex(stage?: string | null): number {
  if (!stage) return 0;
  const normalized = stage.toLowerCase();
  const idx = V2_PROGRESS_STEPS.findIndex((step) => step.stages.includes(normalized));
  if (idx >= 0) return idx;
  if (["failed", "cancelled"].includes(normalized)) return V2_PROGRESS_STEPS.length - 1;
  return 0;
}

function formatElapsed(startedAt?: string | Date | null): string {
  if (!startedAt) return "00:00";
  const startTime = new Date(startedAt).getTime();
  if (!Number.isFinite(startTime)) return "00:00";
  const elapsedSec = Math.max(0, Math.floor((Date.now() - startTime) / 1000));
  const minutes = Math.floor(elapsedSec / 60)
    .toString()
    .padStart(2, "0");
  const seconds = (elapsedSec % 60).toString().padStart(2, "0");
  return `${minutes}:${seconds}`;
}

function formatLogTime(value?: string): string {
  if (!value) return "--:--:--";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "--:--:--";
  return date.toLocaleTimeString();
}

export function GenerationProgress({
  productTitle,
  startedAt,
  progressMessage,
  currentStage,
  stageLogs,
  onCancel,
  showCancel = false,
  embedded = false,
  cancelButtonText = "Cancel",
  mode,
}: GenerationProgressProps) {
  const [dots, setDots] = useState("");
  const [elapsedTime, setElapsedTime] = useState("00:00");

  useEffect(() => {
    const calculateElapsedTime = () => setElapsedTime(formatElapsed(startedAt));

    calculateElapsedTime();
    const timeInterval = setInterval(calculateElapsedTime, 1000);

    const dotsInterval = setInterval(() => {
      setDots((prev) => {
        if (prev === "...") return "";
        return prev + ".";
      });
    }, 500);

    return () => {
      clearInterval(timeInterval);
      clearInterval(dotsInterval);
    };
  }, [startedAt]);

  const hasV2Logs = !!(stageLogs && stageLogs.length > 0);
  const isV2Flow = hasV2Logs || !!currentStage;
  const latestLog = hasV2Logs ? stageLogs![stageLogs!.length - 1] : null;
  const resolvedProgressMessage =
    latestLog?.message || progressMessage || stageToLegacyMessage(currentStage) || null;

  const v2StepIndex = resolveV2StepIndex(currentStage);
  const legacyStepIndex = resolvedProgressMessage
    ? LEGACY_PROGRESS_STEPS.findIndex((s) => s.msg === resolvedProgressMessage)
    : -1;
  const currentStep = isV2Flow ? v2StepIndex + 1 : legacyStepIndex >= 0 ? legacyStepIndex + 1 : 1;
  const totalSteps = isV2Flow ? V2_PROGRESS_STEPS.length : LEGACY_PROGRESS_STEPS.length;
  const progressPercent = Math.min(100, (currentStep / totalSteps) * 100);
  const stepsToRender = isV2Flow
    ? V2_PROGRESS_STEPS.map((step) => ({ label: step.label }))
    : LEGACY_PROGRESS_STEPS.map((step) => ({ label: step.label }));
  const visibleLogs = hasV2Logs ? stageLogs!.slice().reverse() : [];
  const isMultiView = mode === "multi_ultra";
  const estimatedMinutes = isMultiView ? "15–35 min" : "5–15 min";

  return (
    <div
      className={
        embedded
          ? "rounded-3xl bg-gradient-to-br from-indigo-50/80 to-sky-50/80 p-6"
          : "glass-panel border-indigo-100 bg-gradient-to-br from-indigo-50/80 to-sky-50/80"
      }
    >
      <div className="flex flex-col items-center gap-6 text-center">
        <div className="relative">
          <div className="absolute inset-0 animate-ping rounded-full bg-indigo-400/20" />
          <div className="relative flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-sky-500 shadow-lg shadow-indigo-200">
            <Sparkles className="h-10 w-10 animate-pulse text-white" />
          </div>
        </div>

        <div className="space-y-2">
          <h3 className="text-xl font-semibold text-slate-900">Creating your AR model{dots}</h3>
          <p className="text-sm text-slate-600">
            {productTitle ? (
              <>
                Generating 3D model for <span className="font-semibold text-slate-900">{productTitle}</span>
              </>
            ) : (
              "This may take a few minutes"
            )}
          </p>
          {isMultiView && (
            <p className="text-xs font-semibold text-indigo-600 bg-indigo-50 border border-indigo-100 rounded-full px-3 py-1 inline-block">
              Multi-View Ultra — estimated {estimatedMinutes}
            </p>
          )}
          {!isMultiView && (
            <p className="text-xs text-slate-500">Estimated: {estimatedMinutes}</p>
          )}
          {startedAt && (
            <p className="text-sm text-slate-600">
              Elapsed: <span className="font-semibold text-indigo-600">{elapsedTime}</span>
            </p>
          )}
        </div>

        <div className="w-full max-w-md space-y-2">
          <div className="flex justify-between text-xs font-medium text-slate-500">
            <span>
              Step {currentStep} of {totalSteps}
            </span>
            <span>{Math.round(progressPercent)}%</span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-indigo-100">
            <div
              className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-sky-500 transition-all duration-500 ease-out"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>

        <div className="w-full max-w-md space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">What we&apos;re doing</p>
          <div className="rounded-2xl border border-indigo-200/60 bg-white/80 px-4 py-3 shadow-sm">
            <div className="space-y-2">
              {stepsToRender.map((step, idx) => {
                const active = idx === currentStep - 1;
                const done = idx < currentStep - 1;
                const pending = idx >= currentStep;
                return (
                  <div
                    key={idx}
                    className={`flex items-center gap-3 text-sm transition-opacity ${pending ? "opacity-50" : ""}`}
                  >
                    {active ? (
                      <Loader2 className="h-5 w-5 flex-shrink-0 animate-spin text-indigo-500" />
                    ) : done ? (
                      <Check className="h-5 w-5 flex-shrink-0 text-emerald-500" />
                    ) : (
                      <Circle className="h-5 w-5 flex-shrink-0 text-slate-300" strokeWidth={2} />
                    )}
                    <span className={active ? "font-medium text-slate-800" : "text-slate-600"}>
                      {step.label}
                      {active && !resolvedProgressMessage ? dots : ""}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {visibleLogs.length > 0 && (
          <div className="w-full max-w-md space-y-2 text-left">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Pipeline Logs
            </p>
            <div className="max-h-72 space-y-2 overflow-auto rounded-2xl border border-slate-200/70 bg-white/85 px-3 py-3 text-xs text-slate-700">
              {visibleLogs.map((log, idx) => (
                <div
                  key={`${log.stage_name}-${log.status}-${idx}`}
                  className="flex items-start justify-between gap-3 border-b border-slate-100 pb-2 last:border-b-0 last:pb-0"
                >
                  <div>
                    <p className="font-semibold text-slate-800">
                      {log.stage_name} · <span className={log.status === 'failed' ? 'text-rose-500' : log.status === 'completed' ? 'text-emerald-600' : 'text-indigo-500'}>{log.status}</span>
                    </p>
                    <p className="text-slate-600">{log.message}</p>
                  </div>
                  <span className="whitespace-nowrap text-slate-400">{formatLogTime(log.created_at)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-2 rounded-2xl border border-slate-200/60 bg-white/60 px-4 py-3 text-xs text-slate-500">
          <p>
            💡 <strong>Tip:</strong> {isMultiView
              ? "Multi-View Ultra can take 15–35 minutes for premium quality. You can safely close this page — we'll keep generating."
              : "You can close this page and come back later — generation keeps running, and the product will show as ready when you return."}
          </p>
        </div>

        {showCancel && onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="mt-2 rounded-2xl border border-rose-200 bg-white px-6 py-3 text-sm font-semibold text-rose-600 shadow-sm transition-colors hover:border-rose-300 hover:bg-rose-50"
          >
            {cancelButtonText}
          </button>
        )}
      </div>
    </div>
  );
}
